import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  ContestPartStats,
  ContestPartStatsDto,
  EnrolStats,
  EnrolStatsDto,
  ExamCardStats,
  ExamCardStatsDto,
  LocationExamStats,
  LocationExamStatsDto,
  StatsExportType,
  StatsGroupingType,
  StatsType,
  TeacherExamStats,
  TeacherExamStatsDto,
  UserExamStats,
  UserExamStatsDto
} from '../model/stats/Stats';
import { addPageParams, DEFAULT_PAGE, Page } from '../model/Page';
import { SchoolGrade } from '../model/location/School';
import { ContestTaskType } from '../model/contest/ContestTask';

interface ExportResult {
  url: string;
}

@Injectable({
  providedIn: 'root'
})
export class StatsService {

  constructor(private http: HttpClient) {
  }

  private static readonly CONTEST_ID = 'CONTEST_ID';
  private static readonly COMPETITION_ID = 'COMPETITION_ID';
  private static readonly REGION_ID = 'REGION_ID';
  private static readonly SCHOOL_ID = 'SCHOOL_ID';
  private static readonly SCHOOL_GRADE = 'SCHOOL_GRADE';
  private static readonly USER_ID = 'USER_ID';
  private static readonly STATS_TYPE = 'STATS_TYPE';
  private static readonly STATS_GROUPING = 'STATS_GROUPING';
  private static readonly TASK_TYPE = 'TASK_TYPE';

  private static readonly STATS_BASE_URL = `/contests/${StatsService.CONTEST_ID}/stats/${StatsService.STATS_TYPE}`;
  private static readonly GROUPED_STATS_BASE_URL = `${StatsService.STATS_BASE_URL}/grouping/${StatsService.STATS_GROUPING}`;

  private static readonly EXAM_STATS_BASE_URL = `${StatsService.STATS_BASE_URL}?competition_id=${StatsService.COMPETITION_ID}`;
  private static readonly EXAM_STATS_URL = `${StatsService.EXAM_STATS_BASE_URL}&school_grade=${StatsService.SCHOOL_GRADE}&task_type=${StatsService.TASK_TYPE}`;
  private static readonly EXAM_CARD_STATS_URL = `${StatsService.STATS_BASE_URL}?competition_id=${StatsService.COMPETITION_ID}&region_id=${StatsService.REGION_ID}&school_id=${StatsService.SCHOOL_ID}&user_id=${StatsService.USER_ID}`;
  private static readonly EXAM_TEACHER_STATS_URL = `${StatsService.STATS_BASE_URL}?competition_id=${StatsService.COMPETITION_ID}`;

  private static readonly STATS_ENROL_REGION_URL = `${StatsService.GROUPED_STATS_BASE_URL}?region_id=${StatsService.REGION_ID}`;

  public getEnrolStats(contestId: string, regionId: string, grouping: StatsGroupingType,
                       page: Page<never> = DEFAULT_PAGE): Observable<Page<EnrolStats>> {
    const url = addPageParams(StatsService.STATS_ENROL_REGION_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, StatsType.enrol)
      .replace(StatsService.STATS_GROUPING, grouping)
      .replace(StatsService.REGION_ID, regionId), page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => StatsService.parseEnrolStatsPage(data as Page<EnrolStatsDto>))
      );
  }

  public getRegionExamStats(contestId: string, regionId: string, grouping: StatsGroupingType,
                            page: Page<never> = DEFAULT_PAGE): Observable<Page<LocationExamStats>> {
    const url = addPageParams(StatsService.STATS_ENROL_REGION_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, StatsType.exam)
      .replace(StatsService.STATS_GROUPING, grouping)
      .replace(StatsService.REGION_ID, regionId), page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => StatsService.parseRegionExamStatsPage(data as Page<LocationExamStatsDto>))
      );
  }

  public getContestPartStats(contestId: string): Observable<ContestPartStats> {
    const url = StatsService.GROUPED_STATS_BASE_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, StatsType.contest_part)
      .replace(StatsService.STATS_GROUPING, StatsGroupingType.contest);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => {
          const statsPage = data as Page<ContestPartStatsDto>;
          return statsPage.content?.length ? ContestPartStats.parse(statsPage.content[0]) : new ContestPartStats();
        })
      );
  }

  public getUserExamCardStats(contestId: string, competitionId: string,
                              regionId: string, schoolId: string, userId: string): Observable<ExamCardStats> {
    const url = StatsService.EXAM_CARD_STATS_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, StatsType.user_exam_card)
      .replace(StatsService.COMPETITION_ID, competitionId)
      .replace(StatsService.REGION_ID, regionId)
      .replace(StatsService.SCHOOL_ID, schoolId)
      .replace(StatsService.USER_ID, userId);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => ExamCardStats.parse(data as ExamCardStatsDto))
      );
  }

  public getUserExamStats(competitionId: string, contestId: string, grade: SchoolGrade, type: ContestTaskType,
                          regionId: string | undefined, schoolId: string | undefined,
                          page: Page<never> = DEFAULT_PAGE): Observable<Page<UserExamStats>> {
    let baseUrl = StatsService.EXAM_STATS_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, StatsType.user_exam)
      .replace(StatsService.COMPETITION_ID, competitionId)
      .replace(StatsService.SCHOOL_GRADE, grade)
      .replace(StatsService.TASK_TYPE, type);

    if (regionId) baseUrl += `&region_id=${regionId}`;
    if (schoolId) baseUrl += `&school_id=${schoolId}`;

    const url = addPageParams(baseUrl, page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => StatsService.parseExamStatsPage(data as Page<UserExamStatsDto>))
      );
  }

  public getAllUserExamStats(competitionId: string, contestId: string, regionId: string, schoolId: string | undefined,
                             page: Page<never> = DEFAULT_PAGE): Observable<Page<UserExamStats>> {
    return this.getAllUserExamStatsInternal(contestId, competitionId, regionId, schoolId, page, StatsType.user_exam);
  }

  public getAllUserParticipationStats(competitionId: string, contestId: string, regionId: string, schoolId: string | undefined,
                                      page: Page<never> = DEFAULT_PAGE): Observable<Page<UserExamStats>> {
    return this.getAllUserExamStatsInternal(contestId, competitionId, regionId, schoolId, page, StatsType.user_part);
  }

  private getAllUserExamStatsInternal(contestId: string, competitionId: string,
                                      regionId: string, schoolId: string | undefined,
                                      page: Page<never>, statsType: StatsType) {
    let baseUrl = StatsService.EXAM_STATS_BASE_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, statsType)
      .replace(StatsService.COMPETITION_ID, competitionId);

    if (regionId) baseUrl += `&region_id=${regionId}`;
    if (schoolId) baseUrl += `&school_id=${schoolId}`;

    const url = addPageParams(baseUrl, page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => StatsService.parseExamStatsPage(data as Page<UserExamStatsDto>))
      );
  }

  public exportAllUserExamStats(competitionId: string, contestId: string,
                                regionId: string | undefined, schoolId: string | undefined,
                                grade: SchoolGrade, type: ContestTaskType, exportType: StatsExportType): Observable<string> {
    let url = StatsService.EXAM_STATS_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, StatsType.user_exam)
      .replace(StatsService.COMPETITION_ID, competitionId)
      .replace(StatsService.SCHOOL_GRADE, grade)
      .replace(StatsService.TASK_TYPE, type);

    if (regionId) url += `&region_id=${regionId}`;
    if (schoolId) url += `&school_id=${schoolId}`;
    url += `&export_type=${exportType}`;

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => (data as ExportResult).url)
      );
  }

  public getTeacherExamStats(competitionId: string, contestId: string, regionId: string | undefined, schoolId: string | undefined,
                             page: Page<never> = DEFAULT_PAGE): Observable<Page<TeacherExamStats>> {
    let baseUrl = StatsService.EXAM_TEACHER_STATS_URL
      .replace(StatsService.CONTEST_ID, contestId)
      .replace(StatsService.STATS_TYPE, StatsType.teacher_exam)
      .replace(StatsService.COMPETITION_ID, competitionId);

    if (regionId) baseUrl += `&region_id=${regionId}`;
    if (schoolId) baseUrl += `&school_id=${schoolId}`;

    const url = addPageParams(baseUrl, page);

    return this.http.get(`${environment.api.host}${url}`)
      .pipe(
        map(data => StatsService.parseTeacherStatsPage(data as Page<TeacherExamStatsDto>))
      );
  }

  private static parseEnrolStatsPage(page: Page<EnrolStatsDto>) {
    const content = page.content ? page.content.map(item => EnrolStats.parse(item)) : [];

    return {
      max_items: page.max_items,
      next_token: page.next_token,
      asc: page.asc,
      content: content
    };
  }

  private static parseRegionExamStatsPage(page: Page<LocationExamStatsDto>) {
    const content = page.content ? page.content.map(item => LocationExamStats.parse(item)) : [];

    return {
      max_items: page.max_items,
      next_token: page.next_token,
      asc: page.asc,
      content: content
    };
  }

  private static parseExamStatsPage(page: Page<UserExamStatsDto>) {
    const content = page.content ? page.content.map(item => UserExamStats.parse(item)) : [];

    return {
      max_items: page.max_items,
      next_token: page.next_token,
      asc: page.asc,
      content: content
    };
  }

  private static parseTeacherStatsPage(page: Page<TeacherExamStatsDto>) {
    const content = page.content ? page.content.map(item => TeacherExamStats.parse(item)) : [];

    return {
      max_items: page.max_items,
      next_token: page.next_token,
      asc: page.asc,
      content: content
    };
  }

}
